import { AddIcon, Button, Grid, GridRow } from '@bp/ui-components';
import { FieldArray, useFormikContext } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { LessonEditStatus, useSubjectContainersListQuery } from '../../../../types/planung-graphql-client-defs';
import { LessonTableType } from '../../TimetableVersion/Table/TimetableVersionLessonsTable';
import { LessonFormType, LessonFormUnitType } from '../../types';
import { LessonUnitFormItem } from './LessonUnitFormItem';

type LessonUnitFormProps = { status: LessonTableType['editStatus'] };

export const LessonUnitForm = observer(({ status }: LessonUnitFormProps) => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('SUBJECT_CONTAINER');

  const { values, setFieldTouched } = useFormikContext<LessonFormType>();

  const [{ data }] = useSubjectContainersListQuery({
    context,
  });

  const { classesData } = useLoadBasicData({ pause: false });

  const grades = values.lessonClasses?.map((lc) => classesData?.classes.find((c) => c.uuid === lc.class.uuid)?.grade);

  const subjectContainers = data?.subjectContainers.filter((sc) => {
    if (sc.grades.length === 0) {
      return true;
    }
    return grades ? grades.filter((g) => g).every((g) => (g ? sc.grades.includes(g) : false)) : false;
  });

  return (
    <FieldArray name={'lessonUnit'}>
      {(arrayHelpers) => {
        return (
          <Grid useFormGap>
            {values.lessonUnit?.map((lessonUnit, index) => {
              return (
                <LessonUnitFormItem
                  key={lessonUnit.uuid}
                  disabled={lessonUnit.blocked ?? false}
                  index={index}
                  subjectContainer={subjectContainers}
                  onDelete={() => arrayHelpers.remove(index)}
                />
              );
            })}

            <GridRow spacingBottom='none' spacingTop='none'>
              <Button
                key={`${values.subject?.uuid}-${values.lessonClasses?.length}`}
                disabled={!values.subject?.uuid || !values.lessonClasses?.length || status === LessonEditStatus.Blocked}
                hierarchy={'tertiary'}
                icon={<AddIcon />}
                onClick={async () => {
                  if (values.lessonUnit && values.lessonUnit.length > 0) {
                    for (const index in values.lessonUnit) {
                      await setFieldTouched(`lessonUnit.${index}`);
                    }
                  }
                  const lessonUnit: LessonFormUnitType = {
                    uuid: v4(),
                    count: null,
                    factor: null,
                    duration: null,
                    blocked: false,
                    lessonUnitType: 'subjectHour',
                  };
                  arrayHelpers.push(lessonUnit);
                }}
              >
                {t('lessonUnit.add')}
              </Button>
            </GridRow>
          </Grid>
        );
      }}
    </FieldArray>
  );
});
